import React, { useState, useEffect } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import StatCard from '../common/cards/StatCard';
import { formatCurrency } from '../../utils/formatters';
import { AlertTriangle, Activity, Clock, Sparkles, RefreshCw, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { analyzeChurnAI } from '../../services/apiService';
import { exportChurnPDF } from '../../utils/churnPdfExport';

const COLORS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#d946ef', '#f43f5e'];
const CITY_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b'];

export default function ChurnDashboard({ biStats, theme }) {
    const [aiData, setAiData] = useState(null);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [aiError, setAiError] = useState(null);
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [selectedEarlyChurnYear, setSelectedEarlyChurnYear] = useState(null);
    const [selectedInstallerYear, setSelectedInstallerYear] = useState('todos');

    // Initial fetch of cached analysis
    useEffect(() => {
        if (!biStats) return;
        const fetchAiCache = async () => {
            try {
                // Call without 'force' to get cached result
                const res = await analyzeChurnAI(false);
                if (res && res.data) {
                    setAiData(res.data);
                }
            } catch (err) {
                // Se der erro 429 mas tiver data, ou falhar (ignoramos silenciosamente no load inicial pra não assustar)
                if (err.response?.data?.data) {
                    setAiData(err.response.data.data);
                }
            }
        };
        fetchAiCache();

        if (biStats?.earlyChurnYears?.length > 0 && selectedEarlyChurnYear === null) {
            setSelectedEarlyChurnYear(biStats.earlyChurnYears[0]);
        }
    }, [biStats, selectedEarlyChurnYear]);

    const handleUpdateAi = async () => {
        setIsAiLoading(true);
        setAiError(null);
        try {
            const res = await analyzeChurnAI(true);
            if (res && res.data) {
                setAiData(res.data);
            }
        } catch (err) {
            setAiError(err.response?.data?.message || err.message || "Erro desconhecido. Tente novamente mais tarde.");
            if (err.response?.status === 429 && err.response?.data?.data) {
                setAiData(err.response.data.data);
            }
        } finally {
            setIsAiLoading(false);
        }
    };

    const handlePrevYear = () => {
        const currentIndex = earlyChurnYears.indexOf(selectedEarlyChurnYear);
        if (currentIndex < earlyChurnYears.length - 1) {
            setSelectedEarlyChurnYear(earlyChurnYears[currentIndex + 1]);
        }
    };

    const handleNextYear = () => {
        const currentIndex = earlyChurnYears.indexOf(selectedEarlyChurnYear);
        if (currentIndex > 0) {
            setSelectedEarlyChurnYear(earlyChurnYears[currentIndex - 1]);
        }
    };

    const handleExportPDF = async () => {
        setIsPdfLoading(true);
        try {
            await exportChurnPDF(biStats, aiData, {
                timeline: 'churn-chart-timeline',
                reasons:  'churn-chart-reasons',
                plan:     'churn-chart-plan',
                early:    'churn-chart-early',
                city:     'churn-chart-city',
            });
        } catch (err) {
            console.error('Erro ao gerar PDF:', err);
        } finally {
            setIsPdfLoading(false);
        }
    };

    if (!biStats) return <div className="p-4 text-center">Nenhum dado de retenção disponível.</div>;

    const {
        earlyChurnYears = []
    } = biStats;



    const {
        churnRate, churnRateMonth, previousChurnRate, avgChurn,
        churnReasons, timeline,
        churnByPlan = [], earlyChurn = [], earlyChurnByYear = {}, churnByCidade = [],
        churnByInstaller = [], installerYears = []
    } = biStats;

    const isDark = theme === 'dark';
    const gridColor = isDark ? '#334155' : '#e2e8f0';
    const tickColor = isDark ? '#94a3b8' : '#475569';
    const tooltipStyle = {
        backgroundColor: isDark ? '#1e293b' : '#fff',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        color: isDark ? '#fff' : '#000'
    };

    const totalChurns = churnReasons.reduce((acc, curr) => acc + curr.value, 0);

    // Dados filtrados de Early Churn
    const displayEarlyChurn = selectedEarlyChurnYear && earlyChurnByYear[selectedEarlyChurnYear]
        ? earlyChurnByYear[selectedEarlyChurnYear]
        : earlyChurn;

    const totalEarlyChurns = displayEarlyChurn.reduce((acc, e) => acc + e.cancelamentos, 0);
    const earlyChurnPct = totalChurns > 0 ? ((totalEarlyChurns / totalChurns) * 100).toFixed(1) : '0.0';

    const variation = previousChurnRate !== null && previousChurnRate !== undefined
        ? churnRate - previousChurnRate : null;
    const isVariationPositive = variation > 0;
    const variationColor = isVariationPositive
        ? 'text-red-600 dark:text-red-400'
        : 'text-green-600 dark:text-green-400';

    const churnRateValue = (
        <span className="flex items-center gap-2">
            {churnRate.toFixed(2)}%
            {variation !== null && (
                <span className={`text-sm font-medium align-top ${variationColor}`}>
                    {variation > 0 ? '+' : ''}{variation.toFixed(2)}%
                </span>
            )}
        </span>
    );

    // Tooltip: Churn por Plano
    const TooltipChurnPlan = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;
        const d = payload[0].payload;
        return (
            <div className={`p-3 border rounded shadow-md text-sm ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-black'}`}>
                <p className="font-bold mb-1">{d.fullName || d.name}</p>
                <p>Cancelamentos: <span className="font-semibold text-red-500">{d.churnCount}</span></p>
                <p>Assinantes ativos: <span className="font-semibold">{d.activeCount?.toLocaleString('pt-BR')}</span></p>
                <p>Churn / 1k assinantes: <span className="font-semibold text-orange-500">{d.churnPer1k?.toFixed(2)}</span></p>
                <p>Valor de tabela: <span className="font-semibold">{formatCurrency(d.planValue)}</span></p>
                <p>Receita perdida: <span className="font-semibold text-red-500">{formatCurrency(d.receitaPerdida)}</span></p>
            </div>
        );
    };

    // Tooltip: Early Churn
    const TooltipEarlyChurn = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
        const d = payload[0].payload;
        const pct = totalEarlyChurns > 0 ? ((d.cancelamentos / totalEarlyChurns) * 100).toFixed(1) : '0.0';
        const pctTotal = totalChurns > 0 ? ((d.cancelamentos / totalChurns) * 100).toFixed(1) : '0.0';
        return (
            <div className={`p-3 border rounded shadow-md text-sm ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-black'}`}>
                <p className="font-bold mb-1">{label} de contrato</p>
                <p>Cancelamentos: <span className="font-semibold text-orange-500">{d.cancelamentos}</span></p>
                <p>% dos early churns: <span className="font-semibold">{pct}%</span></p>
                <p>% do total de churns: <span className="font-semibold">{pctTotal}%</span></p>
            </div>
        );
    };

    // Tooltip: Churn por Cidade
    const TooltipChurnCidade = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;
        const d = payload[0].payload;
        return (
            <div className={`p-3 border rounded shadow-md text-sm ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-black'}`}>
                <p className="font-bold mb-1">{d.name}</p>
                <p>Cancelamentos: <span className="font-semibold text-red-500">{d.churnCount}</span></p>
                <p>Por mil assin.: <span className="font-semibold">{d.churnPer1k?.toFixed(2)}</span></p>
                <p>% do total: <span className="font-semibold">{d.churnPct?.toFixed(1)}%</span></p>
                <p>Assinantes ativos: <span className="font-semibold">{d.activeSubscribers?.toLocaleString('pt-BR')}</span></p>
            </div>
        );
    };

    // Tooltip: Churn por Instalador
    const TooltipChurnInstaller = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;
        const d = payload[0].payload;
        return (
            <div className={`p-3 border rounded shadow-md text-sm ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-black'}`}>
                <p className="font-bold mb-1">{d.name}</p>
                <p>Contratos instalados: <span className="font-semibold">{d.totalContracts?.toLocaleString('pt-BR')}</span></p>
                <p>Cancelamentos: <span className="font-semibold text-red-500">{d.displayChurnCount}</span></p>
                <p>% de Churn: <span className="font-semibold" style={{ color: d.barColor }}>{d.displayChurnPct?.toFixed(1)}%</span></p>
            </div>
        );
    };

    // Helper: cor semáforo baseada no % de churn do instalador
    const getInstallerColor = (pct, maxPct) => {
        if (maxPct === 0) return '#22c55e';
        const ratio = pct / maxPct;
        if (ratio <= 0.33) return '#22c55e';   // verde
        if (ratio <= 0.66) return '#f59e0b';   // amarelo
        return '#ef4444';                       // vermelho
    };

    return (
        <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                    icon={<AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400"/>}
                    title="Taxa de Churn (Último Mês)"
                    value={churnRateValue}
                    description={churnRateMonth ? `Mês completo: ${churnRateMonth.split('-').reverse().join('/')}` : "Percentual de evasão (mês completo)"}
                    colorClass="bg-red-100 dark:bg-red-900/50"
                />
                <StatCard
                    icon={<Activity className="h-6 w-6 text-blue-600 dark:text-blue-400"/>}
                    title="Churn Médio (12 Meses)"
                    value={`${(avgChurn || 0).toFixed(2)}%`}
                    description="Média de evasão no último ano"
                    colorClass="bg-blue-100 dark:bg-blue-900/50"
                />
                <StatCard
                    icon={<Clock className="h-6 w-6 text-orange-600 dark:text-orange-400"/>}
                    title="Early Churn (Dentro da Fidelidade)"
                    value={
                        <span className="flex items-baseline gap-2">
                            {totalEarlyChurns.toLocaleString('pt-BR')}
                            <span className="text-sm font-medium text-orange-500">{earlyChurnPct}% do total</span>
                        </span>
                    }
                    description="Cancelamentos antes de completar 12 meses"
                    colorClass="bg-orange-100 dark:bg-orange-900/50"
                />
            </div>

            {/* Early Churn — imediatamente abaixo dos KPIs */}
            {displayEarlyChurn.length > 0 && (
                <div id="churn-chart-early" className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-1">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">Early Churn — Distribuição por Mês de Vigência</h3>

                        {earlyChurnYears.length > 0 && (
                            <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-700 p-1 rounded-lg">
                                <button
                                    onClick={handlePrevYear}
                                    disabled={earlyChurnYears.indexOf(selectedEarlyChurnYear) === earlyChurnYears.length - 1}
                                    className="p-1 hover:bg-white dark:hover:bg-slate-600 rounded shadow-sm disabled:opacity-30 transition-all"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <span className="text-sm font-bold px-2 min-w-[60px] text-center">
                                    {selectedEarlyChurnYear}
                                </span>
                                <button
                                    onClick={handleNextYear}
                                    disabled={earlyChurnYears.indexOf(selectedEarlyChurnYear) === 0}
                                    className="p-1 hover:bg-white dark:hover:bg-slate-600 rounded shadow-sm disabled:opacity-30 transition-all"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>

                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                        Cancelamentos dentro do período de fidelidade (12 meses), agrupados pelo mês de vigência em que ocorreram.
                        Total {selectedEarlyChurnYear}: <span className="font-semibold text-orange-500">{totalEarlyChurns.toLocaleString('pt-BR')}</span> cancelamentos precoces
                        (<span className="font-semibold text-orange-500">{earlyChurnPct}%</span> do total de churns).
                    </p>
                    <div className="h-72 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={displayEarlyChurn} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                <XAxis dataKey="label" tick={{ fill: tickColor, fontSize: 12 }} />
                                <YAxis tick={{ fill: tickColor }} />
                                <Tooltip cursor={{ fill: 'rgba(100,116,139,0.1)' }} content={<TooltipEarlyChurn />} />
                                <Bar dataKey="cancelamentos" name="Cancelamentos" radius={[4, 4, 0, 0]}>
                                    {displayEarlyChurn.map((entry, index) => {
                                        const intensity = Math.round(255 - (index / 12) * 130);
                                        return (
                                            <Cell
                                                key={`cell-early-${index}`}
                                                fill={`rgb(${intensity}, ${Math.round(80 + index * 8)}, 68)`}
                                            />
                                        );
                                    })}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Motivos de Cancelamento + Evolução Histórica */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div id="churn-chart-reasons" className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700 flex flex-col">
                    <h3 className="text-lg font-bold mb-1 text-slate-800 dark:text-slate-200">Motivos de Cancelamento</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Total: {totalChurns.toLocaleString('pt-BR')} cancelamentos registrados</p>
                    <div className="flex-grow min-h-[380px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={churnReasons}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={110}
                                    paddingAngle={2}
                                    dataKey="value"
                                    nameKey="name"
                                    label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                                    labelLine={false}
                                >
                                    {churnReasons.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: isDark ? '#fff' : '#000' }} />
                                <Legend formatter={(value) => value.length > 28 ? value.slice(0, 28) + '…' : value} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div id="churn-chart-timeline" className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700 flex flex-col">
                    <h3 className="text-lg font-bold mb-4 text-slate-800 dark:text-slate-200">Evolução de Cancelamentos (Histórico)</h3>
                    <div className="flex-grow min-h-[380px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={timeline} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                <XAxis dataKey="month" tick={{ fill: tickColor }} />
                                <YAxis yAxisId="left" tick={{ fill: tickColor }} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fill: tickColor }} tickFormatter={(v) => `${v.toFixed(2)}%`} />
                                <Tooltip contentStyle={tooltipStyle} />
                                <Legend />
                                <Line yAxisId="left" type="monotone" dataKey="cancelamentos" name="Cancelamentos" stroke="#ef4444" strokeWidth={3} activeDot={{ r: 8 }} />
                                <Line yAxisId="left" type="monotone" dataKey="vendas" name="Novas Ativações" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" />
                                <Line yAxisId="right" type="monotone" dataKey="churnRate" name="Taxa de Churn" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Churn por Plano */}
            {churnByPlan.length > 0 && (
                <div id="churn-chart-plan" className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700">
                    <h3 className="text-lg font-bold mb-1 text-slate-800 dark:text-slate-200">Churn por Plano</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                        Cancelamentos por 1.000 assinantes ativos nos <strong>últimos 365 dias</strong> — base comum para comparação proporcional entre planos de tamanhos diferentes. Planos com menos de 50 assinantes ativos são excluídos.
                    </p>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={churnByPlan} layout="vertical" margin={{ top: 5, right: 40, left: 20, bottom: 25 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} />
                                <XAxis
                                    type="number"
                                    tick={{ fill: tickColor }}
                                    label={{ value: 'Cancelamentos / 1k assinantes', position: 'insideBottom', offset: -10, fill: tickColor, fontSize: 11 }}
                                    height={40}
                                />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    width={160}
                                    tick={{ fill: tickColor, fontSize: 11 }}
                                    tickFormatter={(v) => v.length > 22 ? v.slice(0, 22) + '…' : v}
                                />
                                <Tooltip cursor={{ fill: 'rgba(100,116,139,0.1)' }} content={<TooltipChurnPlan />} />
                                <Bar dataKey="churnPer1k" name="Churn / 1k assinantes" radius={[0, 4, 4, 0]}>
                                    {churnByPlan.map((entry, index) => (
                                        <Cell key={`cell-plan-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    {/* Tabela resumo */}
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700">
                                    <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium">Plano</th>
                                    <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium text-right">Cancel.</th>
                        <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium text-right">Assinantes</th>
                                    <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium text-right">Churn / 1k</th>
                                    <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium text-right">Valor de Tabela</th>
                                    <th className="pb-2 text-slate-500 dark:text-slate-400 font-medium text-right">Receita Perdida</th>
                                </tr>
                            </thead>
                            <tbody>
                                {churnByPlan.map((p, i) => (
                                    <React.Fragment key={i}>
                                        <tr className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                            <td className="py-2 pr-3 font-medium text-slate-700 dark:text-slate-200">{p.fullName}</td>
                                            <td className="py-2 pr-3 text-right">
                                                <span className="inline-block bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded px-2 py-0.5 font-semibold">{p.churnCount}</span>
                                            </td>
                                            <td className="py-2 pr-3 text-right text-slate-500 dark:text-slate-400">{p.activeCount?.toLocaleString('pt-BR')}</td>
                                            <td className="py-2 pr-3 text-right font-bold text-orange-600 dark:text-orange-400">{p.churnPer1k?.toFixed(2)}</td>
                                            <td className="py-2 pr-3 text-right text-slate-600 dark:text-slate-300">{formatCurrency(p.planValue)}</td>
                                            <td className="py-2 text-right font-semibold text-red-600 dark:text-red-400">{formatCurrency(p.receitaPerdida)}</td>
                                        </tr>
                                        {p.cityBreakdown && p.cityBreakdown.length > 1 && (
                                            <tr className="bg-slate-50 dark:bg-slate-800/60">
                                                <td colSpan={6} className="py-2 px-4">
                                                    <div className="flex flex-wrap gap-2">
                                                        {p.cityBreakdown.map((c, ci) => (
                                                            <span key={ci} className="inline-flex items-center gap-1 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-full px-2 py-0.5">
                                                                <span className="font-medium text-slate-600 dark:text-slate-300">{c.city}</span>
                                                                <span className="text-slate-400">·</span>
                                                                <span className="font-bold text-orange-500">{c.churnPer1k?.toFixed(1)}/1k</span>
                                                                <span className="text-slate-400">({c.churnCount} cancel. / {c.activeCount?.toLocaleString('pt-BR')} assin.)</span>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}



            {/* Churn por Cidade */}
            {churnByCidade.length > 0 && (
                <div id="churn-chart-city" className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700">
                    <h3 className="text-lg font-bold mb-1 text-slate-800 dark:text-slate-200">Churn por Cidade (Proporcional a Assinantes)</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                        Cancelamentos por mil <strong>assinantes ativos</strong> nos <strong>últimos 365 dias</strong>. Permite comparar o impacto proporcional entre cidades, eliminando viés de cidades com maior base instalada.
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={churnByCidade} layout="vertical" margin={{ top: 5, right: 40, left: 20, bottom: 25 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} />
                                    <XAxis
                                        type="number"
                                        tick={{ fill: tickColor }}
                                        label={{ value: 'Churn / mil assin.', position: 'insideBottom', offset: -10, fill: tickColor, fontSize: 11 }}
                                        height={40}
                                    />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        width={130}
                                        tick={{ fill: tickColor, fontSize: 12 }}
                                    />
                                    <Tooltip cursor={{ fill: 'rgba(100,116,139,0.1)' }} content={<TooltipChurnCidade />} />
                                    <Bar dataKey="churnPer1k" name="Churn / mil hab." radius={[0, 4, 4, 0]}>
                                        {churnByCidade.map((entry, index) => (
                                            <Cell key={`cell-city-${index}`} fill={CITY_COLORS[index % CITY_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="overflow-x-auto flex items-start">
                            <table className="w-full text-sm text-left">
                                <thead>
                                    <tr className="border-b border-slate-200 dark:border-slate-700">
                                        <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium">Cidade</th>
                                        <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium text-right">Assinantes</th>
                                        <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium text-right">Cancel.</th>
                                        <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium text-right">/mil assin.</th>
                                        <th className="pb-2 text-slate-500 dark:text-slate-400 font-medium text-right">% Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {churnByCidade.map((c, i) => (
                                        <tr key={i} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                            <td className="py-2 pr-3 font-medium text-slate-700 dark:text-slate-200 flex items-center gap-1">
                                                <span
                                                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: CITY_COLORS[i % CITY_COLORS.length] }}
                                                />
                                                {c.name}
                                            </td>
                                            <td className="py-2 pr-3 text-right text-slate-500 dark:text-slate-400">{c.activeSubscribers?.toLocaleString('pt-BR')}</td>
                                            <td className="py-2 pr-3 text-right">
                                                <span className="inline-block bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded px-2 py-0.5 font-semibold">{c.churnCount}</span>
                                            </td>
                                            <td className="py-2 pr-3 text-right font-semibold text-slate-700 dark:text-slate-200">{c.churnPer1k?.toFixed(2)}</td>
                                            <td className="py-2 text-right text-slate-600 dark:text-slate-300">{c.churnPct?.toFixed(1)}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Churn por Instalador */}
            {churnByInstaller.length > 0 && (() => {
                // Filtra dados pelo ano selecionado
                const filteredInstaller = churnByInstaller.map(inst => {
                    if (selectedInstallerYear === 'todos') {
                        return { ...inst, displayChurnPct: inst.churnPct, displayChurnCount: inst.churnCount };
                    }
                    const yd = inst.byYear[selectedInstallerYear];
                    return yd
                        ? { ...inst, displayChurnPct: yd.churnPct, displayChurnCount: yd.churnCount }
                        : { ...inst, displayChurnPct: 0, displayChurnCount: 0 };
                }).sort((a, b) => a.displayChurnPct - b.displayChurnPct);

                const maxPct = Math.max(...filteredInstaller.map(i => i.displayChurnPct), 0);
                const chartData = filteredInstaller.map(inst => ({
                    ...inst,
                    barColor: getInstallerColor(inst.displayChurnPct, maxPct),
                }));

                return (
                    <div id="churn-chart-installer" className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow border border-slate-200 dark:border-slate-700">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-1">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">Churn por Instalador</h3>
                            </div>
                            {/* Filtro de ano */}
                            <div className="flex items-center gap-2">
                                <label htmlFor="installer-year-filter" className="text-xs text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap">Filtrar por ano:</label>
                                <select
                                    id="installer-year-filter"
                                    value={selectedInstallerYear}
                                    onChange={e => setSelectedInstallerYear(e.target.value)}
                                    className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="todos">Todos os anos</option>
                                    {installerYears.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                            Percentual de clientes que cancelaram a assinatura, agrupado pelo instalador responsável.
                            Instaladores com menos de 5 contratos são excluídos. Ordenado do <strong>menor para o maior churn</strong> (melhores primeiro).
                        </p>

                        {/* Gráfico de barras horizontal */}
                        <div style={{ height: Math.max(240, chartData.length * 36 + 40) }} className="w-full mb-6">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 60, left: 10, bottom: 25 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} />
                                    <XAxis
                                        type="number"
                                        domain={[0, maxPct > 0 ? Math.ceil(maxPct * 1.15) : 10]}
                                        tick={{ fill: tickColor }}
                                        tickFormatter={v => `${v}%`}
                                        label={{ value: '% de Churn', position: 'insideBottom', offset: -10, fill: tickColor, fontSize: 11 }}
                                        height={40}
                                    />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        width={120}
                                        tick={{ fill: tickColor, fontSize: 12 }}
                                        tickFormatter={v => v.length > 18 ? v.slice(0, 18) + '…' : v}
                                    />
                                    <Tooltip cursor={{ fill: 'rgba(100,116,139,0.1)' }} content={<TooltipChurnInstaller />} />
                                    <Bar dataKey="displayChurnPct" name="% Churn" radius={[0, 4, 4, 0]}>
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-inst-${index}`} fill={entry.barColor} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Ranking de Melhores Instaladores */}
                        <div>
                            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
                                <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                                Ranking de Melhores Instaladores (menor churn)
                            </h4>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-700">
                                            <th className="pb-2 pr-2 text-slate-500 dark:text-slate-400 font-medium w-8">#</th>
                                            <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium">Instalador</th>
                                            <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium text-right">Contratos</th>
                                            <th className="pb-2 pr-3 text-slate-500 dark:text-slate-400 font-medium text-right">Cancelamentos</th>
                                            <th className="pb-2 text-slate-500 dark:text-slate-400 font-medium text-right">% Churn</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {chartData.map((inst, i) => {
                                            const isBest = i < 3;
                                            return (
                                                <tr key={i} className={`border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 ${isBest ? 'bg-green-50/60 dark:bg-green-900/10' : ''}`}>
                                                    <td className="py-2 pr-2">
                                                        {i === 0 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-400 text-white text-xs font-bold">1</span>}
                                                        {i === 1 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-400 text-white text-xs font-bold">2</span>}
                                                        {i === 2 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-700 text-white text-xs font-bold">3</span>}
                                                        {i > 2 && <span className="text-slate-400 text-xs pl-1">{i + 1}</span>}
                                                    </td>
                                                    <td className="py-2 pr-3 font-medium text-slate-700 dark:text-slate-200">
                                                        {inst.name}
                                                        {isBest && <span className="ml-1 text-xs text-green-600 dark:text-green-400 font-semibold">✓</span>}
                                                    </td>
                                                    <td className="py-2 pr-3 text-right text-slate-500 dark:text-slate-400">{inst.totalContracts.toLocaleString('pt-BR')}</td>
                                                    <td className="py-2 pr-3 text-right">
                                                        <span className="inline-block bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded px-2 py-0.5 font-semibold">{inst.displayChurnCount}</span>
                                                    </td>
                                                    <td className="py-2 text-right">
                                                        <span
                                                            className="inline-block rounded px-2 py-0.5 font-bold text-white text-xs"
                                                            style={{ backgroundColor: inst.barColor }}
                                                        >
                                                            {inst.displayChurnPct?.toFixed(1)}%
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* AI Analysis Section */}
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow border border-slate-200 dark:border-slate-700 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                    <Sparkles className="w-48 h-48" />
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 relative z-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg text-indigo-600 dark:text-indigo-400">
                            <Sparkles className="h-6 w-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-200">Diagnóstico Inteligente</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Análise gerada por Inteligência Artificial</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <button
                            onClick={handleExportPDF}
                            disabled={isPdfLoading}
                            title="Baixar relatório em PDF"
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download className={`h-4 w-4 ${isPdfLoading ? 'animate-bounce' : ''}`} />
                            {isPdfLoading ? 'Gerando PDF...' : 'Baixar PDF'}
                        </button>
                        <button
                            onClick={handleUpdateAi}
                            disabled={isAiLoading}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw className={`h-4 w-4 ${isAiLoading ? 'animate-spin' : ''}`} />
                            {isAiLoading ? 'Analisando...' : 'Atualizar Análise'}
                        </button>
                    </div>
                </div>

                {aiError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                        {aiError}
                    </div>
                )}

                {!aiData && !isAiLoading ? (
                    <div className="text-center py-12 text-slate-500 flex flex-col items-center">
                        <Sparkles className="h-12 w-12 text-slate-300 mb-3" />
                        <p>Nenhuma análise gerada recentemente.</p>
                        <p className="text-sm">Clique em Atualizar para diagnosticar os dados de retenção.</p>
                    </div>
                ) : (
                    <div className="relative z-10">
                        {aiData?.generatedAt && (
                            <div className="mb-4 text-xs font-medium text-slate-400 bg-slate-100 dark:bg-slate-800/50 inline-block px-2.5 py-1 rounded-full">
                                Gerado em: {new Date(aiData.generatedAt).toLocaleString('pt-BR')}
                            </div>
                        )}
                        <div className={`prose dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 ${isAiLoading ? 'opacity-50' : ''}`}>
                            <ReactMarkdown
                                components={{
                                    h3: ({node, ...props}) => <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mt-6 mb-3 flex items-center gap-2" {...props} />,
                                    p: ({node, ...props}) => <p className="leading-relaxed mb-4" {...props} />,
                                    ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-4 space-y-2" {...props} />,
                                    li: ({node, ...props}) => <li className="pl-1" {...props} />
                                }}
                            >
                                {aiData?.content || ''}
                            </ReactMarkdown>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
